// Cookie storage utilities for DVC Calculator
// Handles saving and loading user data to/from browser cookies

import { DVCContract } from './dvcLogic';

const COOKIE_NAMES = {
  CONTRACTS: 'dvc_contracts',
  VACATIONS: 'dvc_vacations'
};

// Cookie expiration: 1 year from now
const COOKIE_EXPIRY_DAYS = 365;

/**
 * Set a cookie with JSON data
 * @param {string} name - Cookie name
 * @param {any} value - Value to store (will be JSON stringified)
 * @param {number} days - Days until expiration (default: 365)
 */
export const setCookie = (name, value, days = COOKIE_EXPIRY_DAYS) => {
  try {
    const expires = new Date();
    expires.setTime(expires.getTime() + (days * 24 * 60 * 60 * 1000));
    
    const jsonValue = JSON.stringify(value);
    document.cookie = `${name}=${encodeURIComponent(jsonValue)};expires=${expires.toUTCString()};path=/;SameSite=Lax`;
    
    return true;
  } catch (error) {
    console.warn('Failed to set cookie:', error);
    return false;
  }
};

/**
 * Get a cookie value and parse as JSON
 * @param {string} name - Cookie name
 * @returns {any|null} Parsed JSON value or null if not found
 */
export const getCookie = (name) => {
  try {
    const nameEQ = name + "=";
    const ca = document.cookie.split(';');
    
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === ' ') c = c.substring(1, c.length);
      if (c.indexOf(nameEQ) === 0) {
        const encodedValue = c.substring(nameEQ.length, c.length);
        const jsonValue = decodeURIComponent(encodedValue);
        return JSON.parse(jsonValue);
      }
    }
    return null;
  } catch (error) {
    console.warn('Failed to get cookie:', error);
    return null;
  }
};

/**
 * Delete a cookie
 * @param {string} name - Cookie name
 */
export const deleteCookie = (name) => {
  document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;`;
};

/**
 * Save contracts to cookies
 * @param {Array} contracts - Array of contract objects
 * @returns {boolean} Success status
 */
export const saveContracts = (contracts) => {
  if (!Array.isArray(contracts)) {
    console.warn('Invalid contracts data - must be an array');
    return false;
  }
  
  return setCookie(COOKIE_NAMES.CONTRACTS, contracts);
};

/**
 * Load contracts from cookies and reconstruct DVCContract instances
 * @returns {Array} Array of DVCContract instances or empty array
 */
export const loadContracts = () => {
  const contractsData = getCookie(COOKIE_NAMES.CONTRACTS);
  
  if (!Array.isArray(contractsData)) {
    return [];
  }
  
  // Reconstruct DVCContract instances from plain objects
  return contractsData.map(data => {
    try {
      // Create new DVCContract instance with the saved data
      return new DVCContract(
        data.id,
        data.homeResort,
        data.useYearMonth,
        data.annualPoints,
        data.contractYear,
        data.pointsRemaining || {}
      );
    } catch (error) {
      console.warn('Failed to reconstruct contract:', data, error);
      // Return a plain object as fallback
      return data;
    }
  });
};

/**
 * Save vacations to cookies
 * @param {Array} vacations - Array of vacation objects
 * @returns {boolean} Success status
 */
export const saveVacations = (vacations) => {
  if (!Array.isArray(vacations)) {
    console.warn('Invalid vacations data - must be an array');
    return false;
  }
  
  return setCookie(COOKIE_NAMES.VACATIONS, vacations);
};

/**
 * Load vacations from cookies
 * @returns {Array} Array of vacation objects or empty array
 */
export const loadVacations = () => {
  const vacations = getCookie(COOKIE_NAMES.VACATIONS);
  return Array.isArray(vacations) ? vacations : [];
};

/**
 * Clear all DVC data from cookies
 */
export const clearAllData = () => {
  deleteCookie(COOKIE_NAMES.CONTRACTS);
  deleteCookie(COOKIE_NAMES.VACATIONS);
};

/**
 * Check if cookies are supported
 * @returns {boolean} True if cookies are supported
 */
export const areCookiesSupported = () => {
  try {
    const testKey = 'dvc_test_cookie';
    setCookie(testKey, 'test');
    const result = getCookie(testKey) === 'test';
    deleteCookie(testKey);
    return result;
  } catch (error) {
    return false;
  }
};

/**
 * Get storage info for debugging
 * @returns {Object} Storage information
 */
export const getStorageInfo = () => {
  const contracts = loadContracts();
  const vacations = loadVacations();
  
  return {
    cookiesSupported: areCookiesSupported(),
    contractsCount: contracts.length,
    vacationsCount: vacations.length,
    lastSaved: new Date().toISOString()
  };
};
